import { Link } from "react-router-dom";
import { BlogPageLayout } from "@/components/landing/BlogPageLayout";
import { SEO } from "@/components/SEO";
import { BreadcrumbNav } from "@/components/seo/BreadcrumbNav";
import { SchemaMarkup } from "@/components/seo/SchemaMarkup";
import { ArrowRight, Calendar, ImageIcon, Quote } from "lucide-react";
import deanHeadshot from "@/assets/dean-thorpe-headshot.jpg";
import goNoGoScreenshot from "@/assets/blog-go-no-go.png";
import masterDraftScreenshot from "@/assets/blog-master-draft.png";

const BASE = "https://sorvaine.com";
const URL = `${BASE}/blog/why-i-built-sorvaine`;
const PUBLISHED = "2026-05-06";

/**
 * Image placeholder — swap `src` later when assets are provided.
 */
function Figure({
  label,
  caption,
  aspect = "16/9",
  src,
  alt,
  fit = "cover",
}: {
  label: string;
  caption?: string;
  aspect?: "16/9" | "4/3" | "1/1" | "3/4" | "auto";
  src?: string;
  alt?: string;
  fit?: "cover" | "contain";
}) {
  const isContain = fit === "contain";
  return (
    <figure className="my-10">
      <div
        className={`overflow-hidden rounded-xl border border-gray-200 ${isContain ? "bg-white" : "bg-gray-50"} ${aspect === "3/4" ? "mx-auto max-w-[420px]" : "w-full"}`}
        style={aspect === "auto" ? undefined : { aspectRatio: aspect }}
      >
        {src ? (
          <img
            src={src}
            alt={alt ?? label}
            className={`w-full ${isContain ? "h-auto object-contain" : "h-full object-cover object-top"}`}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-gray-400">
            <ImageIcon className="mb-2 h-8 w-8" />
            <span className="text-xs font-semibold tracking-[0.12em] uppercase">{label}</span>
            <span className="mt-1 text-[11px] text-gray-400">Image to be added</span>
          </div>
        )}
      </div>
      {caption && <figcaption className="mt-3 text-center text-xs text-gray-400">{caption}</figcaption>}
    </figure>
  );
}

export default function WhyIBuiltSorvaine() {
  return (
    <BlogPageLayout>
      <SEO
        title="From Plan Z to Plan A: Why I Built Sorvaine | Dean Thorpe, Founder"
        description="The story behind Sorvaine — the first AI-powered bid and tender response platform built in Dubai. By founder Dean Thorpe."
        canonical={URL}
      />
      <SchemaMarkup
        type="article"
        title="From Plan Z to Plan A: Why I Built Sorvaine"
        description="The story behind Sorvaine — the first AI-powered bid and tender response platform built in Dubai."
        datePublished={PUBLISHED}
        url={URL}
      />

      <main className="pt-28 pb-20 max-w-[780px] mx-auto px-6">
        <BreadcrumbNav
          items={[
            { name: "Home", url: BASE },
            { name: "Blog", url: `${BASE}/blog` },
            { name: "Why I Built Sorvaine", url: URL },
          ]}
        />

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-primary/80">Founder Story</span>
          <span className="flex items-center gap-1 text-[11px] text-gray-400">
            <Calendar className="w-3 h-3" />
            6 May 2026
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 leading-[1.1]">
          From Plan Z to Plan A: Why I Built Sorvaine
        </h1>
        <p className="mt-4 text-sm text-gray-500">
          By <span className="font-medium text-gray-700">Dean Thorpe</span>, Founder
        </p>

        {/* Hero — Dean headshot */}
        <Figure
          label="Hero — Dean Thorpe headshot"
          caption="Dean Thorpe, Founder of Sorvaine."
          aspect="3/4"
          src={deanHeadshot}
          alt="Dean Thorpe, Founder of Sorvaine"
        />

        {/* Intro */}
        <div className="space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>I didn't plan to build a software company.</p>
          <p>
            In July 2025, I left my product management role at NatWest Bank and moved to Dubai with my partner. I had
            a role lined up at a proptech company before I'd even landed. New city, new life, everything in order.
          </p>
          <p>By November, I'd been made redundant.</p>
        </div>

        {/* The search */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">The search</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>What followed was one of the hardest periods of my life — and I don't say that lightly.</p>
          <p>
            I started applying. Hundreds of applications a week. Product management roles, senior positions, things I
            was genuinely qualified for. The responses were almost nonexistent. No interviews. No rejections even —
            just silence. And that silence is its own kind of punishment.
          </p>
          <p>
            It was the first time in my adult life I'd been out of work for any meaningful stretch. Since my first
            job, I'd always had somewhere to be, something to work on, a reason to get up. Suddenly I didn't. And that
            absence of purpose was genuinely draining in a way I hadn't anticipated. Every rejection — every
            application that disappeared into the void, every interview that went nowhere — felt like a punch in the
            face. Not because I couldn't handle rejection, but because these were jobs I knew I could do with my eyes
            closed.
          </p>
          <p>
            Dubai's hiring market made it worse. End of year budget freezes. Ramadan slowing everything down. The
            uncertainty rippling out from the conflict in the region. The market wasn't moving and there was nothing I
            could do about it.
          </p>
          <p>
            Then on Christmas Day 2025, I tore my calf muscle. A level 4 tear — the kind that puts you on crutches
            for six weeks. So there I was: unemployed, immobile, in a city far from home, watching the new year
            arrive from a sofa.
          </p>
        </div>

        {/* What kept me going */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">What kept me going</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>
            My mindset was simple, even if living it wasn't: keep pushing. Keep digging deeper. Because if I stopped,
            what was going to change? Nothing. I'm the person responsible for making things happen in my own life. If
            I stop, nothing happens. So I didn't stop.
          </p>
          <p>
            To stay sharp — and honestly, to stay sane — I built things. On crutches, from home, just me and a
            laptop. I made two mobile apps. One tracked food expiry dates to reduce waste. The other photographed the
            condition of cars, rental properties and items before handing them over, protecting both parties if
            something went wrong later. Small projects. Useful ideas. But hobbies, not a business.
          </p>
          <p>By the end of 2025 I still hadn't had a single interview.</p>
        </div>

        {/* The lightbulb moment */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">The lightbulb moment</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>Then one evening I watched my partner try to pull together information for a tender response.</p>
          <p>
            She spent nearly a week on it. Hunting through old emails. Chasing colleagues for content they'd already
            written before. Rewriting answers from scratch that existed somewhere — she just couldn't find them. A
            week. For information that should have taken an hour.
          </p>
          <p>
            I sat there watching her work and thought: why doesn't a tool exist that does this properly? Not just
            search. Not just a document store. Something that actually understands what's being asked, finds the right
            content from everything you've already written, and drafts the response for you.
          </p>
          <p className="text-xl font-semibold tracking-tight text-gray-900">
            That was the moment. December 5th, 2025. I started building Sorvaine.
          </p>
        </div>

        {/* Ten hours a day */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">Ten hours a day</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>
            I built it the way you build something when you have nothing else to do and everything to prove. Ten hour
            days. Every day. Weekends included. Still on crutches for the first six weeks.
          </p>
          <p>
            January 2026 felt slow. February picked up. Then Ramadan hit and the market went quiet again. I was still
            applying for jobs alongside building — still hoping something would materialise. I got some interviews.
            Nothing came of them.
          </p>
          <p>
            After Ramadan I thought things would pick up. Then the war escalated. And somewhere in April, sitting with
            another round of interviews going nowhere, I had a different kind of moment.
          </p>
          <p>
            Sorvaine was always my Plan Z. The thing I was building on the side while I figured out my next job. But
            Plan Z was becoming Plan A faster than I'd expected — and faster than I was ready for.
          </p>
          <p>
            I made a decision. I set up the company legally in April. If I was going to do this, I was going to do it
            properly.
          </p>
        </div>

        {/* Proving a point */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">Proving a point</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>
            One piece of feedback I kept receiving from interviews stuck with me: not enough experience doing
            zero-to-one builds. Building something from nothing. I didn't fully understand it at the time — I'd
            shipped products, led teams, delivered at scale. But I took the feedback seriously.
          </p>
          <p>So I decided to do a zero-to-one build. My own. From scratch.</p>
          <p>
            Being a product manager is not an easy job. But being every job at once is something else entirely. I am
            the CEO, the CTO, the CPO, the CMO, the CFO — and I only have one brain to consult on every single
            decision. There's no one to sense-check with. No one to push back. No one to tell you that you're
            overthinking it, or that you're not thinking enough.
          </p>
          <p>
            The hardest hat to wear isn't the technical one or the commercial one. It's being human. The self-doubt is
            relentless. The number of times I've changed the strategy, changed the UI, changed the design, changed
            direction entirely — not because it was wrong, but because I'd looked at it again with fresh eyes and
            convinced myself there was a better way. That cycle is exhausting when you're the only person in the
            room.
          </p>
          <p>But you keep going. Because stopping doesn't fix the doubt — it just confirms it.</p>
          <p>Five months of ten-hour days. And in May 2026, Sorvaine launched.</p>
        </div>

        {/* If you're in the same position */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">If you're in the same position</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>
            If you're reading this and any of it sounds familiar — out of work, time and savings ticking down,
            interviews going nowhere, a side project sitting on your laptop that you keep telling yourself you'll
            "get back to properly one day" — this part is for you.
          </p>
          <p>Take the leap. Go and build it.</p>
          <p>
            We always talk ourselves out of it by focusing on the risk. But what is the real risk? Staying exactly
            where you are. Waiting for permission. Waiting for the perfect moment. Waiting for someone else to hand
            you an opportunity while the pressure quietly builds inside you.
          </p>
          <p>
            You don't need a co-founder, a round of funding, or a perfect plan. You need a problem you actually care
            about and the discipline to show up every day and chip away at it. Five months ago I had a half-built
            prototype, a lot of doubt, and a pair of crutches. Today I have a launched product, real users, and a
            company.
          </p>
          <p>
            Whatever you're working on quietly on the side — that might be your Plan Z right now. But Plan Z has a
            habit of becoming Plan A when you stop waiting and start building. Back yourself.
          </p>
        </div>


        {/* What Sorvaine does */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">Anyway — let's talk about what Sorvaine actually does</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>Sorvaine is AI-powered bid and tender response software.</p>
          <p>
            You upload your RFP or tender document. Sorvaine extracts the requirements, runs a Go/No-Go assessment to
            tell you whether the bid is worth pursuing, builds your proposal outline, and generates a first draft —
            pulling from your own past proposals, case studies, team profiles and company content stored in your
            Knowledge Vault.
          </p>
        </div>

        {/* Screenshot — Go/No-Go */}
        <Figure
          label="Screenshot — Go/No-Go verdict"
          caption="Sorvaine's Go/No-Go verdict — risk signals, positive signals and recommended actions."
          src={goNoGoScreenshot}
          alt="Sorvaine Go/No-Go verdict screen showing risk signals, positive signals and recommended actions"
          aspect="auto"
          fit="contain"
        />

        <div className="mt-2 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>
            The draft isn't generic. It's built from your words, your wins, your voice. Sorvaine finds the answers
            you've already written and puts them in the right place. No more hunting through old emails. No more
            rewriting from scratch at midnight.
          </p>
        </div>

        {/* Screenshot — Master Draft */}
        <Figure
          label="Screenshot — Master Draft (vault-backed sections)"
          caption="Sorvaine's Master Draft — vault-backed sections with linked client requirements and vault sources."
          src={masterDraftScreenshot}
          alt="Sorvaine Master Draft view showing a proposal section with linked client requirements and vault sources"
          aspect="auto"
          fit="contain"
        />

        <div className="mt-2 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>It's the tool I wish my partner had that evening.</p>
        </div>

        {/* Why Dubai */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">Why Dubai</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>
            Sorvaine is the first RFP response SaaS founded and built in Dubai. That wasn't a strategic decision — it
            was circumstance. But it feels right.
          </p>
          <p>
            Businesses everywhere respond to tenders. Professional services firms, technology companies, consultancies,
            managed service providers — from London to Riyadh to Singapore, the same problem exists. Bid managers
            buried in documents. Teams rewriting content they've already written. Good businesses losing contracts on
            paper because they ran out of time to put their best case forward.
          </p>
          <p>Sorvaine is built for all of them. The GCC is where it started. The world is where it's going.</p>
        </div>

        {/* For the bid managers */}
        <h2 className="mt-14 text-2xl font-semibold tracking-tight text-gray-900">For the bid managers and proposal teams</h2>
        <div className="mt-4 space-y-6 text-gray-600 leading-relaxed text-[17px]">
          <p>
            If you spend your days responding to tenders — Sorvaine is built for you. Not for the enterprise with a
            hundred-person bid team and a six-figure software budget. For the bid manager who's been up until midnight
            formatting a Word document. For the proposal team that wins work but struggles to scale. For the business
            that's good at what it does but loses on paper.
          </p>
          <p className="text-xl font-semibold tracking-tight text-gray-900">
            Stop hunting for answers. Start winning more.
          </p>
        </div>

        {/* Author bio */}
        <div className="mt-14 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500 italic leading-relaxed">
            Dean Thorpe is the founder of Sorvaine, the first AI-powered bid and tender response platform built in
            Dubai.
          </p>
        </div>

        {/* Related reading */}
        <section className="mt-14">
          <h2 className="text-lg font-semibold text-gray-900">Related reading</h2>
          <div className="mt-4 space-y-2">
            <Link to="/resources/how-to-respond-to-an-rfp" className="block text-sm text-primary hover:underline">How to Respond to an RFP — step by step →</Link>
            <Link to="/resources/ai-rfp-software" className="block text-sm text-primary hover:underline">How AI Is Transforming RFP Responses →</Link>
            <Link to="/resources/proposal-content-library" className="block text-sm text-primary hover:underline">Build a Proposal Content Library that scales →</Link>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="mt-14 rounded-xl bg-primary p-10 md:p-14 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white leading-tight">
            Stop hunting for answers. Start winning more.
          </h2>
          <div className="mt-7 flex justify-center">
            <Link to="/subscribe">
              <button className="inline-flex items-center justify-center px-8 h-12 rounded-md bg-white hover:bg-white/95 text-primary text-sm font-semibold transition-all duration-200 hover:-translate-y-[1px] active:scale-[0.97] shadow-md">
                Get started
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </Link>
          </div>
        </section>
      </main>
    </BlogPageLayout>
  );
}
